import { AccessiblePrice } from '@/components/AccessiblePrice'
import { DetailsList } from '@/components/DetailsList/DetailsList'
import { RedeemedCampaignTypeV2 } from '@/services/graphql/generated'
import { type ShopSession } from '@/services/shopSession/ShopSession.types'
import { Text } from 'ui/src/components/Text/Text'
import { ToggleTip } from 'ui/src/components/Tooltip/ToggleTip'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'

type Props = { cart: ShopSession['cart'] }

export function OrderBreakdown({ cart }: Props) {
  const cartEntries = cart.items.map(({ productOffer }) => ({
    id: productOffer.id,
    name: productOffer.product.displayNameFull,
    productName: productOffer.product.name,
    tier: productOffer.variant.displayNameTier,
    subType: productOffer.variant.displayNameSubtype,
    price: productOffer.cost.baseInsuranceGross,
    discounts: productOffer.cost.discountsV2.map(({ itemDiscount }) => itemDiscount),
    insuranceAddons: productOffer.addons
      .filter((addon) => addon.isSelected)
      .map((addon) => ({
        id: addon.id,
        name: addon.variant.displayName,
        price: addon.grossCost,
        coverageText: addon.coverageText,
      })),
  }))

  return (
    <DetailsList.Root gap="md">
      {cartEntries.map((entry) => {
        return (
          <li key={entry.id}>
            <ul className={yStack({ gap: 'xxs' })}>
              <DetailsList.Item>
                <DetailsList.Label asChild>
                  <span className={sprinkles({ color: 'textPrimary', flex: { _: 1, xs: 'auto' } })}>{entry.name}</span>
                </DetailsList.Label>
                <DetailsList.Value className={sprinkles({ color: 'textPrimary' })}>
                  <AccessiblePrice price={{ currencyCode: entry.price.currencyCode, amount: entry.price.amount }} />
                </DetailsList.Value>
              </DetailsList.Item>

              {entry.insuranceAddons.map((addon) => (
                <DetailsList.Item key={addon.id}>
                  <DetailsList.Label className={xStack({ alignItems: 'center', gap: 'xxs' })}>
                    {addon.name}
                  </DetailsList.Label>
                  <DetailsList.Value>
                    <AccessiblePrice price={{ currencyCode: addon.price.currencyCode, amount: addon.price.amount }} />
                  </DetailsList.Value>
                </DetailsList.Item>
              ))}

              {entry.discounts.map((discount) => (
                <DetailsList.Item key={discount.campaignCode}>
                  <DetailsList.Label>{discount.displayName}</DetailsList.Label>
                  <DetailsList.Value className={xStack({ gap: 'xxs' })}>
                    <Text as="span" size="xs">
                      {discount.displayValue}
                    </Text>
                    {discount.tooltip && (
                      <ToggleTip>
                        <Text size="xs" color="textNegative" align="center">
                          {discount.tooltip}
                        </Text>
                      </ToggleTip>
                    )}
                  </DetailsList.Value>
                </DetailsList.Item>
              ))}
            </ul>
          </li>
        )
      })}

      {isForeverDiscount(cart.redeemedCampaignV2) && (
        <DetailsList.Item>
          <DetailsList.Label>Forever</DetailsList.Label>
          <DetailsList.Value>{cart.redeemedCampaignV2.displayValue}</DetailsList.Value>
        </DetailsList.Item>
      )}
    </DetailsList.Root>
  )
}

function isForeverDiscount(
  campaign: ShopSession['cart']['redeemedCampaignV2'],
): campaign is NonNullable<typeof campaign> {
  return campaign?.type === RedeemedCampaignTypeV2.Referral
}
